import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  ListSubheader,
  Toolbar, 
  CssBaseline, 
  AppBar, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl,
  useMediaQuery,
  IconButton,
  Avatar,
  CircularProgress,
  Menu,
  Divider,
  Tooltip
} from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

import LogoutIcon from '@mui/icons-material/Logout';
import SchoolIcon from '@mui/icons-material/School';
// import StorageIcon from '@mui/icons-material/Storage';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enGB } from 'date-fns/locale';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { getSettings, updateSettings } from './services/firestoreService';
import { getThemeTokens, normalizeThemePreferences } from './utils/themePreferences';
// Route-level code splitting to reduce bundle size and speed up first paint
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ExternalDataEntryPage = lazy(() => import('./pages/ExternalDataEntryPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TimetablePage = lazy(() => import('./pages/TimetablePage'));
const StudentsPage = lazy(() => import('./pages/StudentsPage'));
const DailyReportPage = lazy(() => import('./pages/DailyReportPage'));
const WeeklyPlanPage = lazy(() => import('./pages/WeeklyPlanPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MentorsPage = lazy(() => import('./pages/MentorsPage'));

const ReportsOverviewPage = lazy(() => import('./pages/ReportsOverviewPage'));
const DataSeedingPage = lazy(() => import('./pages/DataSeedingPage'));
const CurriculumManagementPage = lazy(() => import('./pages/CurriculumManagementPage'));
const WPMReportPage = lazy(() => import('./pages/WpmReportPage'));
const WPMManagementPage = lazy(() => import('./pages/WpmManagementPage'));
const ParentReportPreviewPage = lazy(() => import('./pages/ParentReportPreviewPage'));


const drawerWidth = 240;

const ColorModeContext = React.createContext({ toggleColorMode: () => {} });
const _defaultShadows = createTheme().shadows;

// Protected Route Component
function ProtectedRoute({ children }) {
  const { currentUser } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function AppContent({ settings, onUpdateSettings }) {
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const theme = useTheme();
  const colorMode = React.useContext(ColorModeContext);
  const currentPath = location.pathname;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [selectedCenter, setSelectedCenter] = React.useState('Hephzi');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleProfileMenuClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // If on login page, don't show the main layout
  if (location.pathname === '/login') {
    return null;
  }

  const canSeeSeed = Boolean(currentUser);

  const navItems = [
    { type: 'section', text: 'Plans' },
    { text: 'Plans Overview', icon: <DashboardIcon />, path: '/' },
    { text: 'Timetable', icon: <CalendarMonthIcon />, path: '/timetable' },
    { text: 'Weekly Plan', icon: <EventNoteIcon />, path: '/weekly-plan' },
    { type: 'section', text: 'Reports' },
    { text: 'Reports Overview', icon: <SchoolIcon />, path: '/reports' },
    { text: 'Daily Report Entry', icon: <AssessmentIcon />, path: '/daily-report' },
    { text: 'WPM Report Entry', icon: <AssessmentIcon />, path: '/wpm-report' },
    { text: 'WPM Management', icon: <ManageSearchIcon />, path: '/wpm-management' },
    { type: 'section', text: 'People' },
    { text: 'Students', icon: <PeopleIcon />, path: '/students' },
    { text: 'Mentors', icon: <PeopleAltIcon />, path: '/mentors' },

    { type: 'section', text: 'Utilities' },
    ...(canSeeSeed ? [{ text: 'Seed Data', icon: <ManageSearchIcon />, path: '/seed' }, { text: 'Curriculum', icon: <ManageSearchIcon />, path: '/curriculum' }] : []),
    { text: 'Parent Report Preview', icon: <AssessmentIcon />, path: '/parent-report-preview' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' }
  ];

  const isItemSelected = (path) => {
    if (path === '/') return currentPath === '/' || currentPath === '/dashboard';
    return currentPath === path || currentPath.startsWith(`${path}/`);
  };

  const pageTitle = navItems.find(item => isItemSelected(item.path))?.text || 'Plans Overview';

  const drawer = (
    <div>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 2 
      }}>
        <img 
          src="https://fees.talcworld.com/static/media/logo-light.6d227ed7ba1a9e559495.png" 
          alt="Logo" 
          style={{ 
            height: '40px',
            width: 'auto'
          }} 
        />
      </Box>
      <List
        subheader={
          <ListSubheader disableSticky sx={{ bgcolor: 'transparent' }} />
        }
      >
        {navItems.map((item, index) => {
          if (item.type === 'section') {
            return (
              <ListSubheader
                key={`${item.text}-${index}`}
                disableSticky
                sx={{ bgcolor: 'transparent', color: 'text.secondary', fontWeight: 600, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', mt: 1.5 }}
              >
                {item.text}
              </ListSubheader>
            );
          }

          return (
            <ListItem 
              key={item.text} 
              disablePadding 
              component={Link} 
              to={item.path}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{ 
                color: 'inherit', 
                textDecoration: 'none',
                '& .MuiListItemButton-root': {
                  borderRadius: '8px',
                  mx: 1,
                  '&:hover': {
                    bgcolor: 'primary.light',
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main'
                    }
                  }
                }
              }}
            >
              <ListItemButton selected={isItemSelected(item.path)}>
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0px 1px 4px rgba(0,0,0,0.1)',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            )}
            {!isMobile && (
              <img 
                src="https://fees.talcworld.com/static/media/logo-light.6d227ed7ba1a9e559495.png" 
                alt="Logo" 
                style={{ 
                  height: '40px',
                  width: 'auto',
                  marginRight: '16px'
                }} 
              />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {pageTitle}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedCenter}
                onChange={(e) => setSelectedCenter(e.target.value)}
                sx={{ 
                  bgcolor: 'background.paper',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  }
                }}
              >
                <MenuItem value="Hephzi">Hephzi</MenuItem>
                <MenuItem value="Harlur">Harlur</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title={theme.palette.mode === 'dark' ? 'Light mode' : 'Dark mode'}>
              <IconButton onClick={colorMode.toggleColorMode} color="inherit" size="small">
                {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
            
            <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
              <Avatar 
                alt={currentUser?.displayName || 'User'}
                src={currentUser?.photoURL}
                sx={{ width: 36, height: 36 }}
              >
                {currentUser?.displayName?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {currentUser?.displayName || 'User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {currentUser?.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { 
            width: drawerWidth, 
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
            '& .MuiListItemButton-root': {
              '&:hover': {
                bgcolor: 'primary.light',
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: 'primary.contrastText'
                }
              },
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: 'primary.contrastText'
                },
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }
            }
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, md: 3 },
          bgcolor: 'background.default',
          minHeight: '100vh'
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>
          <Toolbar />
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}><CircularProgress /></Box>}>
            <Routes key={location.pathname}>
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/timetable" element={<ProtectedRoute><TimetablePage /></ProtectedRoute>} />
            <Route path="/weekly-plan" element={<ProtectedRoute><WeeklyPlanPage /></ProtectedRoute>} />
            <Route path="/students" element={<ProtectedRoute><StudentsPage /></ProtectedRoute>} />
            <Route path="/mentors" element={<ProtectedRoute><MentorsPage /></ProtectedRoute>} />
            <Route path="/daily-report" element={<ProtectedRoute><DailyReportPage /></ProtectedRoute>} />
            <Route path="/daily-report-v2" element={<Navigate to="/daily-report" replace />} />
            <Route path="/reports-management" element={<Navigate to="/reports" replace />} />
            <Route path="/report-status" element={<Navigate to="/reports" replace />} />

            <Route path="/academic-progress" element={<Navigate to="/reports" replace />} />
            {/* Dev-only seed page */}
            <Route path="/seed" element={canSeeSeed ? <ProtectedRoute><DataSeedingPage /></ProtectedRoute> : <Navigate to="/" replace />} />
            <Route path="/curriculum" element={canSeeSeed ? <ProtectedRoute><CurriculumManagementPage /></ProtectedRoute> : <Navigate to="/" replace />} />
            <Route path="/reports" element={<ProtectedRoute><ReportsOverviewPage /></ProtectedRoute>} />
            <Route path="/wpm-report" element={<ProtectedRoute><WPMReportPage /></ProtectedRoute>} />
            <Route path="/wpm-management" element={<ProtectedRoute><WPMManagementPage /></ProtectedRoute>} />
            <Route path="/parent-report-preview" element={<ProtectedRoute><ParentReportPreviewPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage settings={settings} onUpdateSettings={onUpdateSettings} /></ProtectedRoute>} />
            <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          </Routes>
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  const [settings, setSettings] = React.useState(null);
  const [mode, setMode] = React.useState(
    () => localStorage.getItem('essence_theme_mode') || 'light'
  );
  const themePreferences = React.useMemo(
    () => normalizeThemePreferences(settings?.themePreferences),
    [settings]
  );
  const themeTokens = React.useMemo(
    () => getThemeTokens(mode, themePreferences),
    [mode, themePreferences]
  );

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsData = await getSettings();
        setSettings(settingsData);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    loadSettings();
  }, []);

  const handleUpdateSettings = React.useCallback(async (settingsData) => {
    try {
      await updateSettings('default', settingsData);
      setSettings(settingsData);
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }, []);

  const colorMode = React.useMemo(() => ({
    toggleColorMode: () => {
      setMode((prev) => {
        const next = prev === 'light' ? 'dark' : 'light';
        localStorage.setItem('essence_theme_mode', next);
        return next;
      });
    },
  }), []);

  const theme = React.useMemo(() => createTheme({
    palette: {
      mode,
      primary: themeTokens.accents.primary,
      secondary: themeTokens.accents.secondary,
      success: themeTokens.accents.success,
      warning: themeTokens.accents.warning,
      error: themeTokens.accents.error,
      info: themeTokens.accents.info,
      background: {
        default: themeTokens.background.default,
        paper: themeTokens.background.paper,
      },
      text: themeTokens.background.text,
      divider: themeTokens.background.divider,
    },
    shape: {
      borderRadius: 12
    },
    typography: {
      fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      h4: { fontWeight: 700, letterSpacing: '-0.01em' },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      body1: { lineHeight: 1.6 },
      body2: { lineHeight: 1.6 }
    },
    shadows: _defaultShadows.map((shadow, index) => {
      if (index === 1) {
        return mode === 'dark'
          ? '0 1px 3px 0 rgba(0,0,0,0.3), 0 1px 2px 0 rgba(0,0,0,0.2)'
          : '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)';
      }
      if (index === 2) {
        return mode === 'dark'
          ? '0 4px 6px -1px rgba(0,0,0,0.3), 0 2px 4px -1px rgba(0,0,0,0.2)'
          : '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)';
      }
      return shadow;
    }),
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            fontWeight: 500
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none'
          }
        }
      },
      MuiTextField: {
        defaultProps: {
          size: 'small'
        }
      },
      MuiSelect: {
        defaultProps: {
          size: 'small'
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: mode === 'dark'
              ? '0 1px 3px 0 rgba(0,0,0,0.3), 0 1px 2px 0 rgba(0,0,0,0.2)'
              : '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)'
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600
          }
        }
      }
    }
  }), [mode, themeTokens]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={enGB}
          dateFormats={{
            fullDate: 'dd/MM/yyyy',
            keyboardDate: 'dd/MM/yyyy',
            shortDate: 'dd/MM/yyyy',
            normalDate: 'dd/MM/yyyy',
          }}
        >
          <AuthProvider>
            <Router>
              <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}><CircularProgress /></Box>}>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/external-entry" element={<ExternalDataEntryPage />} />
                  <Route path="/*" element={<AppContent settings={settings} onUpdateSettings={handleUpdateSettings} />} />
                </Routes>
              </Suspense>
            </Router>
          </AuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;